// comparisonEngine.js
// Utility / engine layer for LIC vs Mutual Fund comparison.

import licPlanConfig from '@/lib/licPlanConfig.json';

export function round2(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

export function formatINR(value) {
  const num = Number(value || 0);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(num);
}

export function formatPct(value, digits = 2) {
  return `${Number(value || 0).toFixed(digits)}%`;
}

export function getYearsArray(startYear, years) {
  return Array.from({ length: years }, (_, i) => Number(startYear) + i);
}

export function getPlan(planKey) {
  return licPlanConfig.plans[planKey] || licPlanConfig.plans.new_endowment;
}

export function getTermBandKey(planKey, policyTermYears) {
  const term = Number(policyTermYears);

  switch (planKey) {
    case 'new_endowment':
      if (term <= 15) return '12_15';
      if (term <= 20) return '16_20';
      return '21_plus';
    case 'new_jeevan_anand':
      if (term <= 15) return '15';
      if (term <= 20) return '16_20';
      return '21_plus';
    case 'jeevan_labh':
      if (term <= 16) return '16';
      if (term <= 21) return '21';
      return '25';
    case 'new_money_back_20':
      return '20';
    default:
      return '16_20';
  }
}

export function getTermBand(planKey, policyTermYears) {
  const plan = getPlan(planKey);
  const key = getTermBandKey(planKey, policyTermYears);
  return plan.termBands[key];
}

export function getHighSaStepUp(planKey, basicSumAssured) {
  const sa = Number(basicSumAssured || 0);
  let stepUp = 0;

  if (sa >= 500000) stepUp += 1;
  // Jeevan Labh allows one more step for >= 10L in the preloaded comparison assumptions.
  if (planKey === 'jeevan_labh' && sa >= 1000000) stepUp += 1;

  return stepUp;
}

export function getDefaultSrbPer1000({ planKey, policyTermYears, basicSumAssured, applyHighSaStepUp = true }) {
  const band = getTermBand(planKey, policyTermYears);
  let base = Number(band?.avgSrbPer1000 || 0);
  if (applyHighSaStepUp) base += getHighSaStepUp(planKey, basicSumAssured);
  return round2(base);
}

export function getDefaultFabPer1000({ planKey, policyTermYears }) {
  const band = getTermBand(planKey, policyTermYears);
  return Number(band?.defaultFabPer1000 || 0);
}

export function calculateVestedBonus({ basicSumAssured, annualBonusPer1000, policyTermYears }) {
  const units = Number(basicSumAssured || 0) / 1000;
  return round2(units * Number(annualBonusPer1000 || 0) * Number(policyTermYears || 0));
}

export function calculateFab({ basicSumAssured, fabPer1000, policyTermYears, minFabEligibilityYears = 15, isPaidUp = false }) {
  if (isPaidUp) return 0;
  if (Number(policyTermYears || 0) < Number(minFabEligibilityYears || 15)) return 0;
  return round2((Number(basicSumAssured || 0) / 1000) * Number(fabPer1000 || 0));
}

export function calculateSurvivalBenefits(planKey, basicSumAssured) {
  const plan = getPlan(planKey);
  const items = (plan.survivalBenefits || []).map((item) => ({
    year: item.year,
    pctOfSa: item.pctOfSa,
    amount: round2((Number(basicSumAssured || 0) * Number(item.pctOfSa || 0)) / 100),
  }));

  const total = items.reduce((sum, item) => sum + item.amount, 0);
  return { items, total: round2(total) };
}

export function getLicMaturityBase(planKey, basicSumAssured) {
  const plan = getPlan(planKey);
  if (plan.benefitType === 'partial_sa_on_maturity') {
    return round2((Number(basicSumAssured || 0) * Number(plan.maturityBasePctOfSa || 0)) / 100);
  }
  return round2(Number(basicSumAssured || 0));
}

export function buildLicCashflows({
  annualPremium,
  premiumPayingTermYears,
  policyTermYears,
  maturityOnly,
  survivalBenefitsItems = [],
}) {
  const years = Number(policyTermYears || 0);
  const ppt = Number(premiumPayingTermYears || 0);
  const premium = Number(annualPremium || 0);
  const cashflows = [];

  for (let year = 1; year <= years; year += 1) {
    let cf = 0;
    if (year <= ppt) cf -= premium;

    const survival = survivalBenefitsItems.find((item) => Number(item.year) === year);
    if (survival) cf += Number(survival.amount || 0);

    if (year === years) cf += Number(maturityOnly || 0);
    cashflows.push(cf);
  }

  return cashflows;
}

export function xirrApprox(cashflows, guess = 0.06) {
  if (!Array.isArray(cashflows) || cashflows.length < 2) return 0;
  const hasPositive = cashflows.some((cf) => cf > 0);
  const hasNegative = cashflows.some((cf) => cf < 0);
  if (!hasPositive || !hasNegative) return 0;

  const years = cashflows.map((_, i) => i + 1);

  function npv(rate) {
    return cashflows.reduce((sum, cf, idx) => sum + cf / Math.pow(1 + rate, years[idx]), 0);
  }

  let rate = guess;
  for (let i = 0; i < 200; i += 1) {
    const f = npv(rate);
    const derivative = cashflows.reduce((sum, cf, idx) => {
      const t = years[idx];
      return sum - (t * cf) / Math.pow(1 + rate, t + 1);
    }, 0);

    if (Math.abs(derivative) < 1e-10) break;
    const newRate = rate - f / derivative;
    if (!Number.isFinite(newRate)) break;
    if (Math.abs(newRate - rate) < 1e-9) {
      rate = newRate;
      break;
    }
    rate = newRate;
  }

  return round2(rate * 100);
}

export function realReturnPct(nominalPct, inflationPct) {
  const nominal = Number(nominalPct || 0) / 100;
  const inflation = Number(inflationPct || 0) / 100;
  const real = ((1 + nominal) / (1 + inflation)) - 1;
  return round2(real * 100);
}

export function inflationAdjustedValue(value, inflationPct, years) {
  return round2(Number(value || 0) / Math.pow(1 + Number(inflationPct || 0) / 100, Number(years || 0)));
}

export function calculateLicProjection(input) {
  const {
    planKey = 'new_endowment',
    basicSumAssured = 1000000,
    annualPremium = 50000,
    policyTermYears = 20,
    premiumPayingTermYears = 20,
    annualBonusMode = 'historical_average',
    manualAnnualBonusPer1000 = null,
    applyHighSaStepUp = true,
    fabMode = 'default_term_ladder',
    manualFabPer1000 = null,
    isPaidUp = false,
    inflationPct = licPlanConfig.assumptions.inflationDefaultPct,
  } = input || {};

  const plan = getPlan(planKey);
  const termBand = getTermBand(planKey, policyTermYears);

  const annualBonusPer1000 = manualAnnualBonusPer1000 != null
    ? Number(manualAnnualBonusPer1000)
    : getDefaultSrbPer1000({ planKey, policyTermYears, basicSumAssured, applyHighSaStepUp });

  const vestedBonus = calculateVestedBonus({ basicSumAssured, annualBonusPer1000, policyTermYears });

  const fabPer1000 = fabMode === 'manual' && manualFabPer1000 != null
    ? Number(manualFabPer1000)
    : getDefaultFabPer1000({ planKey, policyTermYears });

  const fab = calculateFab({
    basicSumAssured,
    fabPer1000,
    policyTermYears,
    minFabEligibilityYears: plan.minFabEligibilityYears,
    isPaidUp,
  });

  const maturityBase = getLicMaturityBase(planKey, basicSumAssured);
  const survivalBenefits = calculateSurvivalBenefits(planKey, basicSumAssured);
  const maturityOnly = round2(maturityBase + vestedBonus + fab);
  const totalPolicyValueIncludingEarlierPayouts = round2(maturityOnly + survivalBenefits.total);

  const totalPremiumPaid = round2(Number(annualPremium || 0) * Number(premiumPayingTermYears || 0));

  const cashflows = buildLicCashflows({
    annualPremium,
    premiumPayingTermYears,
    policyTermYears,
    maturityOnly,
    survivalBenefitsItems: survivalBenefits.items,
  });
  const impliedIrrPct = xirrApprox(cashflows);
  const realReturn = realReturnPct(impliedIrrPct, inflationPct);

  return {
    plan,
    termBand,
    annualBonusMode,
    annualBonusPer1000,
    vestedBonus,
    fabMode,
    fabPer1000,
    fab,
    maturityBase,
    maturityOnly,
    survivalBenefits,
    totalPolicyValueIncludingEarlierPayouts,
    totalPremiumPaid,
    impliedIrrPct,
    realReturnPct: realReturn,
    inflationAdjustedMaturityOnly: inflationAdjustedValue(maturityOnly, inflationPct, policyTermYears),
    inflationAdjustedTotalValue: inflationAdjustedValue(totalPolicyValueIncludingEarlierPayouts, inflationPct, policyTermYears),
    cashflows,
  };
}

export function calculateMutualFundProjection(input) {
  const {
    investmentMode = 'monthly',
    monthlyInvestment = 5000,
    annualInvestment = 60000,
    stepUpPct = 0,
    expectedReturnPct = 12,
    years = 20,
    inflationPct = licPlanConfig.assumptions.inflationDefaultPct,
  } = input || {};

  const months = Number(years || 0) * 12;
  const monthlyRate = (Number(expectedReturnPct || 0) / 100) / 12;
  let corpus = 0;
  let totalInvested = 0;

  if (investmentMode === 'monthly') {
    let sip = Number(monthlyInvestment || 0);
    for (let year = 1; year <= Number(years || 0); year += 1) {
      for (let month = 1; month <= 12; month += 1) {
        corpus = (corpus + sip) * (1 + monthlyRate);
        totalInvested += sip;
      }
      sip *= 1 + Number(stepUpPct || 0) / 100;
    }
  } else {
    let yearly = Number(annualInvestment || 0);
    for (let year = 1; year <= Number(years || 0); year += 1) {
      corpus = (corpus + yearly) * (1 + Number(expectedReturnPct || 0) / 100);
      totalInvested += yearly;
      yearly *= 1 + Number(stepUpPct || 0) / 100;
    }
  }

  const realReturn = realReturnPct(expectedReturnPct, inflationPct);

  return {
    totalInvested: round2(totalInvested),
    finalValue: round2(corpus),
    impliedIrrPct: round2(Number(expectedReturnPct || 0)),
    realReturnPct: realReturn,
    inflationAdjustedFinalValue: inflationAdjustedValue(corpus, inflationPct, years),
    wealthGain: round2(corpus - totalInvested),
    liquidity: 'High',
    flexibility: 'High',
    risk: 'Market-linked',
  };
}

// Conservative LIC death benefit payable if death happens at `yearsElapsed`.
// Death Benefit = MAX(Basic Sum Assured, 10 × Annual Premium) + Accrued Bonus (+ FAB only at final year).
export function calculateLicDeathBenefitAtYear(licInput, yearsElapsed, { includeFab = false } = {}) {
  const {
    planKey = 'new_endowment',
    basicSumAssured = 1000000,
    annualPremium = 50000,
    policyTermYears = 20,
    manualAnnualBonusPer1000 = null,
    applyHighSaStepUp = true,
    fabMode = 'default_term_ladder',
    manualFabPer1000 = null,
    isPaidUp = false,
  } = licInput || {};

  // Sum assured on death = MAX(Basic Sum Assured, 10 × Annual Premium)
  const deathSumAssured = Math.max(Number(basicSumAssured || 0), 10 * Number(annualPremium || 0));

  // Annual bonus rate per ₹1,000 (same resolution used across the engine)
  const annualBonusPer1000 = manualAnnualBonusPer1000 != null
    ? Number(manualAnnualBonusPer1000)
    : getDefaultSrbPer1000({ planKey, policyTermYears, basicSumAssured, applyHighSaStepUp });

  // Accrued bonus till this year = (SA / 1000) × annualBonusPer1000 × yearsElapsed
  const accruedBonus = round2((Number(basicSumAssured || 0) / 1000) * annualBonusPer1000 * Number(yearsElapsed || 0));

  // FAB is a terminal-style bonus – only credit it at the final year.
  let fab = 0;
  if (includeFab) {
    const plan = getPlan(planKey);
    const fabPer1000 = fabMode === 'manual' && manualFabPer1000 != null
      ? Number(manualFabPer1000)
      : getDefaultFabPer1000({ planKey, policyTermYears });
    fab = calculateFab({
      basicSumAssured,
      fabPer1000,
      policyTermYears,
      minFabEligibilityYears: plan.minFabEligibilityYears,
      isPaidUp,
    });
  }

  return {
    deathSumAssured: round2(deathSumAssured),
    annualBonusPer1000,
    accruedBonus,
    fab,
    total: round2(deathSumAssured + accruedBonus + fab),
  };
}

// "What if something happens early?" – compares family payout under LIC vs MF
// at Death in Year 3, Year 10 and the Final Year.
export function calculateDeathScenarioComparison({ licInput, mfInput } = {}) {
  const lic = licInput || {};
  const mf = mfInput || {};

  const policyTermYears = Number(lic.policyTermYears || mf.years || 20);

  // Candidate scenario years: Year 3, Year 10, Final Year (capped to policy term, de-duplicated, ordered)
  const candidateYears = [3, 10, policyTermYears];
  const seen = new Set();
  const orderedYears = [];
  candidateYears.forEach((yr) => {
    const year = Math.min(Number(yr), policyTermYears);
    if (year > 0 && !seen.has(year)) {
      seen.add(year);
      orderedYears.push(year);
    }
  });

  const scenarios = orderedYears.map((year) => {
    const isFinalYear = year === policyTermYears;

    // LIC death payout (FAB only at final year)
    const licDeath = calculateLicDeathBenefitAtYear(lic, year, { includeFab: isFinalYear });
    const licValue = licDeath.total;

    // MF corpus accumulated till that year (reuses existing MF projection logic)
    const mfProjection = calculateMutualFundProjection({ ...mf, years: year });
    const mfValue = mfProjection.finalValue;

    return {
      label: isFinalYear ? `Final Year (Year ${year})` : `Year ${year}`,
      year,
      isFinalYear,
      licValue,
      mfValue,
      gap: round2(licValue - mfValue), // Protection Gap = LIC − MF
      licBreakdown: licDeath,
      mfTotalInvested: mfProjection.totalInvested,
    };
  });

  const earlyScenario = scenarios[0] || null;
  const finalScenario = scenarios[scenarios.length - 1] || null;

  return {
    scenarios,
    earlyProtectionGap: earlyScenario ? Math.abs(earlyScenario.gap) : 0,
    licAdvantageEarly: earlyScenario ? earlyScenario.gap > 0 : false,
    mfAdvantageLater: finalScenario ? finalScenario.gap < 0 : false,
  };
}

export function compareLicVsMutualFund({ licInput, mfInput, inflationPct = 6 }) {
  const lic = calculateLicProjection({ ...licInput, inflationPct });
  const mf = calculateMutualFundProjection({ ...mfInput, inflationPct });
  const deathComparison = calculateDeathScenarioComparison({ licInput, mfInput });

  return {
    lic,
    mutualFund: mf,
    deathComparison,
    differenceInFinalValue: round2(mf.finalValue - lic.totalPolicyValueIncludingEarlierPayouts),
    differenceInInflationAdjustedValue: round2(mf.inflationAdjustedFinalValue - lic.inflationAdjustedTotalValue),
    messages: {
      licGeneral: licPlanConfig.messages.licGeneral,
      mutualFundGeneral: licPlanConfig.messages.mutualFundGeneral,
      comparisonWarnings: licPlanConfig.messages.comparisonWarnings,
    },
  };
}

export default {
  licPlanConfig,
  getPlan,
  getTermBandKey,
  getTermBand,
  getDefaultSrbPer1000,
  getDefaultFabPer1000,
  calculateLicProjection,
  calculateMutualFundProjection,
  calculateLicDeathBenefitAtYear,
  calculateDeathScenarioComparison,
  compareLicVsMutualFund,
  formatINR,
  formatPct,
  realReturnPct,
  inflationAdjustedValue,
};
